import { useState } from 'react'
import { Loader2, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useCreateRole, useDeleteRole, useRoles, useUpdateRole } from '@/features/roles/hooks'
import type { AccountRole } from '@/types/auth'
import { FLOWS, flowLabel, type Flow } from '@/lib/flows'

interface RoleFormState {
  name: string
  flows: Flow[]
}

const emptyRoleForm: RoleFormState = { name: '', flows: [] }

export function RolesTab() {
  const { data: roles, isLoading, isError } = useRoles()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<AccountRole | null>(null)
  const [form, setForm] = useState<RoleFormState>(emptyRoleForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [listError, setListError] = useState<string | null>(null)
  const [deletingRole, setDeletingRole] = useState<AccountRole | null>(null)

  const createRole = useCreateRole()
  const updateRole = useUpdateRole()
  const deleteRole = useDeleteRole()
  const saving = createRole.isPending || updateRole.isPending

  const openCreate = () => {
    setEditing(null)
    setForm(emptyRoleForm)
    setFormError(null)
    setDialogOpen(true)
  }

  const openEdit = (role: AccountRole) => {
    setEditing(role)
    setForm({ name: role.name, flows: role.flows })
    setFormError(null)
    setDialogOpen(true)
  }

  const toggleFlow = (flow: Flow, checked: boolean) => {
    setForm((f) => ({
      ...f,
      flows: checked ? [...f.flows, flow] : f.flows.filter((x) => x !== flow),
    }))
  }

  const onSave = () => {
    setFormError(null)
    if (!form.name.trim()) {
      setFormError('Give the role a name.')
      return
    }
    if (form.flows.length === 0) {
      setFormError('Grant the role at least one flow.')
      return
    }
    const body = { name: form.name.trim(), flows: form.flows }
    const opts = {
      onSuccess: () => setDialogOpen(false),
      onError: (err: Error) => setFormError(err.message),
    }
    if (editing) {
      updateRole.mutate({ id: editing.id, body }, opts)
    } else {
      createRole.mutate(body, opts)
    }
  }

  // Deleting a role signs out every user who holds it — always confirm.
  const onDelete = (role: AccountRole) => {
    setListError(null)
    deleteRole.mutate(role.id, {
      onSuccess: () => setDeletingRole(null),
      onError: (err) => {
        setListError(err.message)
        setDeletingRole(null)
      },
    })
  }

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" /> Roles
            </CardTitle>
            <CardDescription>
              Custom roles name a set of flows — the people assigned to them see (and can work in)
              only those flows. Editing a role's flows signs its users out so the change applies on
              next sign-in.
            </CardDescription>
          </div>
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> New role
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <p className="text-destructive text-sm">Failed to load roles.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Role</TableHead>
                    <TableHead>Flows</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(roles ?? []).map((role) => (
                    <TableRow key={role.id}>
                      <TableCell>
                        <div className="flex items-center gap-2 font-medium">
                          {role.name}
                          {role.is_system && <Badge>Admin · full access</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {role.is_system ? (
                            <span className="text-muted-foreground text-sm">Everything</span>
                          ) : (
                            role.flows.map((flow) => (
                              <Badge key={flow} variant="outline">
                                {flowLabel(flow)}
                              </Badge>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{role.user_count}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={role.is_system}
                            title={
                              role.is_system ? 'The Admin role cannot be modified' : 'Edit role'
                            }
                            onClick={() => openEdit(role)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={role.is_system || deleteRole.isPending}
                            title={
                              role.is_system ? 'The Admin role cannot be deleted' : 'Delete role'
                            }
                            onClick={() => setDeletingRole(role)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {listError && <p className="text-destructive mt-3 text-sm">{listError}</p>}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'Create role'}</DialogTitle>
            <DialogDescription>
              Name the role and pick the flows it can access — users with this role see only those
              flows.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-2">
              <Label htmlFor="r-name">Role name</Label>
              <Input
                id="r-name"
                value={form.name}
                placeholder="e.g. Front Desk"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label>Flows</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {FLOWS.map((flow) => (
                  <label
                    key={flow.key}
                    className="flex cursor-pointer items-center gap-2 rounded-md border p-2.5 text-sm"
                  >
                    <Checkbox
                      checked={form.flows.includes(flow.key)}
                      onCheckedChange={(checked) => toggleFlow(flow.key, checked === true)}
                    />
                    {flow.label}
                  </label>
                ))}
              </div>
            </div>
            {formError && <p className="text-destructive text-sm">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? 'Save changes' : 'Create role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — deleting a role revokes its users' sessions. */}
      <Dialog open={!!deletingRole} onOpenChange={(o) => !o && setDeletingRole(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete role {deletingRole?.name}?</DialogTitle>
            <DialogDescription>
              Every user assigned to this role loses access and is signed out. This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingRole(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteRole.isPending}
              onClick={() => deletingRole && onDelete(deletingRole)}
            >
              {deleteRole.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
